import { getRandomUuid } from "./browserApis";
import { getCorrelationId } from "./correlationId";
import { type ClientOs } from "./registry/clientOses";
import { type Client } from "./registry/clients";
import { type ComponentName } from "./registry/componentNames";
import { type ComponentType } from "./registry/componentTypes";
import { type EventName } from "./registry/eventNames";
import { type EventType } from "./registry/eventTypes";
import { type PageName } from "./registry/pageNames";
import { nextId } from "./snowflake";

/**
 * What may appear as a value in `extraInfo`.
 *
 * Scalars only. Everything in `extraInfo` is `JSON.stringify`'d into the queue
 * inside the `navigator.locks` callback, and a function, DOM node, or circular
 * structure throws there — inside a fire-and-forget `track`, so the rejection
 * is swallowed and the whole queue write is lost with no signal. Objects and
 * arrays also bloat the payload against the byte cap, silently trimming older
 * events out of the queue.
 *
 * `undefined` is allowed because omitting a key and passing `undefined` are the
 * same intent at a call site; `JSON.stringify` drops those keys anyway.
 *
 * This is a guardrail, not a guarantee — `as any` still gets through, and
 * values sourced from untyped API data are unchecked at runtime. It stops the
 * accidental case: passing an object, an array, or a whole entity by reflex.
 */
export type IAnalyticsExtraInfoValue =
  | string
  | number
  | boolean
  | null
  | undefined;

export interface IAnalyticsEvent {
  /**
   * A time-ordered identifier for the analytics event, generated with a snowflake ID generator.
   * Suitable for ordering events, but NOT guaranteed unique — use `uniqueEventId` for idempotency / de-duplication.
   */
  id: string;
  /**
   * A v4 UUID that uniquely identifies the analytics event. Use this (not `id`) as the idempotency key when de-duplicating events downstream.
   */
  uniqueEventId: string;
  /**
   * The type of event that occurred (e.g. "click", "route_change").  Describes the type of user interaction that triggered the event.
   * For a more specific, contextual name of the event, use `eventName` instead.
   */
  eventType: EventType;
  /**
   * The contextual name of the event that occurred (e.g. "approve_bill", "change_job").
   */
  eventName: EventName;
  /**
   * The timestamp when the event was created, in ISO 8601 format. This is generated at the time of event creation to accurately reflect when the event occurred.
   * i.e. when `buildEvent` is called, not when the event is sent to the analytics backend.
   */
  eventTimestamp: string;
  /**
   * Always false for client-side event.
   */
  isServer: false;
  /**
   * A unique identifier that can be used to correlate related analytics events. This is useful for tracking user sessions or flows across multiple events.
   */
  correlationId: string;
  /**
   * The type of client that generated the event (e.g. "desktop_browser", "mobile_browser", "tablet_browser", "mobile_webview").
   */
  client: Client;
  /**
   * The operating system of the client that generated the event (e.g. "windows", "macos", "linux", "android", "ios").
   */
  clientOs: ClientOs;
  /**
   * The type of component that generated the event (e.g. "button", "icon_button", "link", "modal", "tab", "panel").
   */
  componentType: ComponentType | null;
  /**
   * The name of the component that generated the event (e.g. "change_job_button", "ocr_itemized_button"). Used to provide more specific context about the user interaction that triggered the event.
   */
  componentName: ComponentName | null;
  /**
   * The name of the page where the event occurred (e.g. "bill_detail", "lien_waiver_review"). This can be used to provide context about the user's current location within the application.
   */
  pageName: PageName | null;
  /**
   * The jobId associated with the event, if applicable.
   */
  jobId: number | null;
  /**
   * The URL of the page where the event occurred, as `origin` + `pathname`.
   * Not `window.location.href`: the query string is dropped and any share token
   * in the path is replaced with `:shareToken` (see `redactSharePath`).
   */
  pageUrl: string;
  /**
   * The page the user navigated away from, derived from `document.referrer` and
   * put through the same redaction as `pageUrl` — origin + pathname, tokens
   * masked, query string dropped. `null` when there is no referrer or it cannot
   * be parsed.
   */
  referrerUrl: string | null;
  /**
   * The path of the page where the event occurred, with share tokens replaced by
   * `:shareToken`. Not yet a true route template (e.g. `/jobs/:jobId/bills/:billId`) —
   * that needs the deepest matched route, which means every nested route
   * participating, so real ids other than share tokens still appear here.
   */
  routeTemplate: string;
  /**
   * A record of additional information that can be included with the analytics event.
   * This can be used to provide context or metadata about the event, such as user actions, feature flags, or other relevant data.
   *
   * Never null: `buildEvent` always contributes `selected_job_id` from the
   * enrichers, so every event carries at least that key.
   */
  extraInfo: Record<string, IAnalyticsExtraInfoValue>;
  /**
   * The `globalUserId` of the current user.  From this, you can determine their `builderId` and `loginType`.
   */
  userOrgLinkId: number | null;
  /**
   * The builderId of the current user.
   */
  builderId: number | null;
  /**
   * The impersonation field indicates whether the event was generated while a user was impersonating another user.
   * If the user is impersonating, this field will contain the **type** of the impersonated user (e.g. BTAdmin or BuilderImpersonatingOwner); otherwise, it will be null.
   */
  impersonation: string | null;
}

/**
 * Input for building an analytics event via the `useAnalytics` hook.
 * This input is combined with the `IAnalyticsContextData` context data and enrichers to create a complete analytics event.
 */
export interface IAnalyticsEventInput {
  /**
   * The type of event that occurred (e.g. "click", "route_change").  Describes the type of user interaction that triggered the event.
   * For a more specific, contextual name of the event, use `eventName` instead.
   */
  eventType: EventType;
  /**
   * The contextual name of the event that occurred (e.g. "approve_bill", "change_job").
   */
  eventName: EventName;
  /**
   * The type of component that generated the event (e.g. "button", "icon_button", "link", "modal", "tab", "panel").
   */
  componentType?: ComponentType | null;
  /**
   * The name of the component that generated the event (e.g. "change_job_button", "ocr_itemized_button"). Used to provide more specific context about the user interaction that triggered the event.
   */
  componentName?: ComponentName | null;
  /**
   * The jobId associated with the event, if applicable.
   */
  jobId?: number | null;
  /**
   * Currently this is equal to `document.referrer`, but in the future it may be set to the previous route (during the route change event).
   */
  referrerUrl?: string | null;
  /**
   * A record of additional information that can be included with the analytics event.
   * This can be used to provide context or metadata about the event, such as user actions, feature flags, or other relevant data.
   */
  extraInfo?: Record<string, IAnalyticsExtraInfoValue>;
}

/**
 * Context data that is provided by the `AnalyticsProvider` and can be used to enrich analytics events.
 * Set this data at the top-level of your components to provide context for descendant analytics events.
 */
export interface IAnalyticsContextData {
  /**
   * The name of the page where the event occurred (e.g. "bill_detail", "lien_waiver_review"). This can be used to provide context about the user's current location within the application.
   */
  pageName?: PageName | null;
  /**
   * The jobId associated with the event, if applicable.
   */
  jobId?: number | null;
  /**
   * Currently this is equal to `document.referrer`, but in the future it may be set to the previous route (during the route change event).
   */
  referrerUrl?: string | null;
  /**
   * A record of additional information that can be included with the analytics event.
   * This can be used to provide context or metadata about the event, such as user actions, feature flags, or other relevant data.
   */
  extraInfo?: Record<string, IAnalyticsExtraInfoValue>;
}

/**
 * Functions that provide additional top-level information to be included in analytics events.
 * These functions are used internally by the `useAnalytics` hook to enrich events before they are sent to the analytics backend.
 * Implementations of these functions should be provided by the application using the `AnalyticsProvider` component.
 */
export interface IAnalyticsEnrichers {
  /**
   * The `globalUserId` of the current user.  From this, you can determine their `builderId` and `loginType`.
   * @returns The `globalUserId` of the current user
   */
  getUserOrgLinkId: () => number | null;
  /**
   * The builderId of the current user.
   * @returns The `builderId` associated to the current user/session.
   */
  getBuilderId: () => number | null;
  /**
   * The type of client that generated the event (e.g. "desktop_browser", "mobile_browser", "tablet_browser", "mobile_webview").
   * @returns The type of client that generated the event.
   */
  getClient: () => Client;
  /**
   * The operating system of the client that generated the event (e.g. "windows", "macos", "linux", "android", "ios").
   * @returns The operating system of the client that generated the event, per the User Agent string.
   */
  getClientOs: () => ClientOs;
  /**
   * Indicates whether the event was generated while a user was impersonating another user.
   * @returns The impersonation identifier if the user was impersonating another user, otherwise `null`.
   */
  getImpersonation: () => string | null;
  /**
   * The job currently selected in the job picker, folded into every event's
   * `extraInfo` as `selected_job_id`.
   *
   * Distinct from the event's top-level `jobId`, which is the job the *record*
   * belongs to. The two differ whenever a user acts on a record from a job
   * other than the selected one — on an "All Jobs" list, that is most of the
   * time.
   *
   * The host may report a non-job value here to describe the *kind* of
   * selection, and BTNet does: its job picker uses `0` for "All Jobs" and
   * negatives for its other modes, all passed through as-is because which mode
   * the user was in is the signal. Treat this as a selection descriptor, not a
   * foreign key — filter before joining it to a job.
   *
   * @returns The current selection, or `null` if the host has no concept of one.
   */
  getSelectedJobId: () => number | null;
}

/**
 * A 36-character path segment. `routes.ts` declares `:shareToken(.{36})` on 26
 * external routes (bids, deposits, purchase orders, lien waivers, lead
 * proposals, owner invoices, media, schedules, messages, task print), and two
 * prefix helpers put one at the head of whole subtrees.
 *
 * Deliberately shape-based rather than an allowlist of known routes: an
 * allowlist has to be maintained against every new external route by every
 * team, and failing to update it fails open. This fails closed — a legitimate
 * 36-char identifier is redacted too, which costs some analytics fidelity but
 * never leaks a credential.
 */
const SHARE_TOKEN_SEGMENT = /(?<=\/)[^/]{36}(?=\/|$)/g;

/**
 * Replaces share tokens in a URL path with `:shareToken`.
 *
 * A share token is an unauthenticated access credential and it lives in the
 * path, not the query string — so stripping the query alone would not contain
 * it. Left intact it reaches localStorage on the recipient's device (external
 * share pages have no logout to clear the queue), the ingestion POST, and the
 * warehouse, where anyone with read access holds a replayable link to the
 * shared record.
 */
function redactSharePath(pathname: string): string {
  return pathname.replace(SHARE_TOKEN_SEGMENT, ":shareToken");
}

/**
 * Builds `pageUrl` with tokens redacted and the query string dropped.
 *
 * The query string goes because nothing downstream reads it and it is an
 * open-ended surface — today it carries flags like `sendToAccounting`, but any
 * future param would become an unreviewed disclosure.
 */
function buildSafePageUrl(): string {
  const { origin, pathname } = window.location;
  return `${origin}${redactSharePath(pathname)}`;
}

/**
 * Applies the same redaction to a full URL supplied from outside — currently
 * `document.referrer`, which `AppRouteChange` feeds into every `route_change`
 * event app-wide.
 *
 * Without this, navigating away from an external share page hands the next
 * event the full previous URL, token and query string included: the exact
 * disclosure `pageUrl`/`routeTemplate` redaction prevents, arriving one event
 * later. Nothing in Clients.App or Clients.Api sets a `Referrer-Policy`, so the
 * browser default sends the whole URL on same-origin navigations.
 *
 * Returns `null` for an empty or unparseable value so a malformed referrer
 * cannot leak through unredacted.
 */
function redactReferrerUrl(referrer: string | null): string | null {
  if (!referrer) {
    return null;
  }
  try {
    const url = new URL(referrer);
    return `${url.origin}${redactSharePath(url.pathname)}`;
  } catch {
    return null;
  }
}

/**
 * Key names whose values are secrets, whatever the surrounding word.
 *
 * Deliberately not a bare `/auth/` substring: `AuthoredBy` is a real domain
 * concept here (`BillAuthoredByInfo`, `commonComponents/entity/authoredBy`), so
 * `authorName` or `authoredBy` in `extraInfo` would be silently blanked — a
 * data bug wearing a security policy's clothes. Hence `auth(?!or)`, with
 * `authoriz` spelled out ahead of it so `authorization` is still caught: it
 * begins with the same `author` the exclusion is there to protect.
 *
 * Shape-based rather than an allowlist of known-bad keys, for the same reason
 * `SHARE_TOKEN_SEGMENT` is: an allowlist has to be maintained against every new
 * call site by every team, and forgetting fails open.
 */
const SECRET_KEY =
  /token|secret|password|credential|api[-_]?key|authoriz|auth(?!or)/i;

const REDACTED = "[redacted]";

/**
 * Blanks the values of keys that name a secret, keeping the keys themselves.
 *
 * Keys are kept so the redaction is visible: a dropped key is
 * indistinguishable from a call site that never sent one, so the warehouse
 * could not tell "we protected you" from "nobody instrumented this".
 *
 * `extraInfo` is an open bag written by every team, and the values reach
 * localStorage on the user's device, the ingestion POST, and the warehouse.
 * `payment_token` is already live in v3 (`LienWaiverReviewPresentational`), and
 * the very same token is scrubbed from `pageUrl`/`referrerUrl`/`routeTemplate`
 * by `redactSharePath` — leaving it legible here would make that pointless,
 * since both ride the same event to the same place.
 */
function redactSecretKeys(
  extraInfo: Record<string, IAnalyticsExtraInfoValue>,
): Record<string, IAnalyticsExtraInfoValue> {
  const redacted: Record<string, IAnalyticsExtraInfoValue> = {};
  for (const [key, value] of Object.entries(extraInfo)) {
    redacted[key] = SECRET_KEY.test(key) ? REDACTED : value;
  }
  return redacted;
}

/**
 * Creates a complete analytics event by combining the input data, context data, and enrichers.
 * This function is used internally by the `useAnalytics` hook to build events before they are sent to the analytics backend.
 * @param input The input data for the analytics event.
 * @param context The context data provided by the `AnalyticsProvider`.
 * @param enrichers The enrichers used to add additional information to the event.
 * @returns The complete analytics event.
 */
export function buildEvent(
  input: IAnalyticsEventInput,
  context: IAnalyticsContextData,
  enrichers: IAnalyticsEnrichers,
): IAnalyticsEvent {
  return {
    id: nextId(),
    uniqueEventId: getRandomUuid(),
    eventType: input.eventType,
    eventName: input.eventName,
    eventTimestamp: new Date().toISOString(),
    isServer: false,
    correlationId: getCorrelationId(),
    client: enrichers.getClient(),
    clientOs: enrichers.getClientOs(),
    componentType: input.componentType ?? null,
    componentName: input.componentName ?? null,
    pageName: context.pageName ?? null,
    jobId: input.jobId ?? context.jobId ?? null,
    pageUrl: buildSafePageUrl(),
    referrerUrl: redactReferrerUrl(
      input.referrerUrl ?? context.referrerUrl ?? null,
    ),
    routeTemplate: redactSharePath(window.location.pathname),
    // The enricher's selection goes in first, so a page's ContextProvider or an
    // individual call site can override it when it knows better. Always
    // emitted — including when the enricher returns null — so the warehouse
    // column is present on every event rather than sparse.
    // Redacted after the merge, not before, so a secret cannot slip through by
    // arriving on whichever side wins.
    extraInfo: redactSecretKeys({
      selected_job_id: enrichers.getSelectedJobId(),
      ...context.extraInfo,
      ...input.extraInfo,
    }),
    userOrgLinkId: enrichers.getUserOrgLinkId(),
    builderId: enrichers.getBuilderId(),
    impersonation: enrichers.getImpersonation(),
  };
}
